<script setup>

import {router} from "@inertiajs/vue3"
import {computed, ref} from "vue"
import {helpers} from "@/Services/helpers.js"
import CCustomizedProductDetailsModal from "@/Pages/Cart/Partials/CCustomizedProductDetailsModal.vue"
import CLink from "@/Components/CLink.vue"

const detailsDialog = ref(false)
const dialogMetadata = ref(null)
const emit = defineEmits(['close-drawer'])
const debounceTimers = ref({})

const props = defineProps({
    cartItems: Array,
    compact: {
        type: Boolean,
        default: false
    }
})

const groupedCartItems = computed(() => {
    return props.cartItems.reduce((groups, item) => {
        const productTitle = item.product_variation.product.title
        const attributes = item.product_variation.attribute_values

        if (!groups[productTitle]) {
            groups[productTitle] = {variations: {}, single: []}
        }


        if (attributes.length === 2) {
            // Produto com 2 atributos (ex: Cor e Tamanho)
            const color = attributes.find(attribute => attribute.attribute_id === 1).title
            const size = attributes.find(attribute => attribute.attribute_id === 2).title

            if (!groups[productTitle].variations[color]) {
                groups[productTitle].variations[color] = []
            }

            groups[productTitle].variations[color].push({
                ...item,
                size: size
            })
        }

        if (attributes.length === 1) {
            // Produto com apenas 1 atributo (ex: Tamanho ou Cor)
            groups[productTitle].single.push(item)
        }

        if (attributes.length === 0) {
            groups[productTitle].single.push(item)
        }

        groups[productTitle].productReference = item.product_variation.product.reference
        groups[productTitle].manufacturing_days = item.product_variation.product.manufacturing_days

        return groups
    }, {})
})

const deleteCartItem = function (cartItem) {
    router.get(
        route('cart.delete', cartItem),
        {},
        {
            preserveState: true,
        }
    )
}

const deleteSessionItem = function (variantProductId) {
    router.get(
        route('cart.sessionDelete', variantProductId),
        {},
        {
            preserveState: true,
        }
    )
}

const updateVariantQuantity = function (cartItem) {
    const itemId = cartItem.id || cartItem.product_variation_id || JSON.stringify(cartItem)

    if (debounceTimers.value[itemId]) {
        clearTimeout(debounceTimers.value[itemId])
    }

    debounceTimers.value[itemId] = setTimeout(() => {
        delete debounceTimers.value[itemId]

        router.post(
            route('cart.update'),
            {cartItem},
            {
                preserveState: true
            }
        )
    }, 500)
}


const calculateVariationsQuantity = function (colorCartGroup, color) {
    let totalGroupQuantity = 0

    // Verifica se a cor existe no grupo
    if (colorCartGroup[color]) {
        colorCartGroup[color].forEach(variation => {
            totalGroupQuantity += variation.quantity
        })
    }

    return totalGroupQuantity
}

const calculateVariationsPrice = function (colorCartGroup, color) {
    let totalGroupPrice = 0

    // Verifica se a cor existe no grupo
    if (colorCartGroup[color]) {
        colorCartGroup[color].forEach(cartItem => {
            totalGroupPrice += cartItem.subtotal
        })
    }

    return totalGroupPrice
}

const routeToProductDetails = function (cartItem, variationSlug) {
    let productSlug = ''
    let tagSlug = 'produto'

    if (cartItem?.single?.[0]?.product_variation?.product?.slug) {
        productSlug = cartItem.single[0].product_variation.product.slug
        tagSlug = cartItem.single[0].product_variation.product?.main_tag?.slug || 'produto'
    } else if (cartItem?.variations) {
        const firstColorKey = Object.keys(cartItem.variations)[0]
        if (firstColorKey && cartItem.variations[firstColorKey][0]?.product_variation?.product?.slug) {
            productSlug = cartItem.variations[firstColorKey][0].product_variation.product.slug
            tagSlug = cartItem.variations[firstColorKey][0].product_variation.product.main_tag?.slug || 'produto'
        }
    } else if (cartItem.product_variation?.product?.slug) {
        productSlug = cartItem.product_variation.product.slug
        tagSlug = cartItem.product_variation.product?.main_tag?.slug || 'produto'
    }

    const slug = variationSlug ? productSlug + '?color=' + variationSlug : productSlug

    if (slug) {
        return route('product.view', {menuItemSlug: tagSlug, product: slug})
    } else {
        return null
    }
}

const anyVariationHasInvalidPrice = function (colorCartGroup, color) {
    let hasInvalidPrice = false

    if (colorCartGroup[color]) {
        colorCartGroup[color].forEach(cartItem => {
            if (!cartItem.price || cartItem.price === 0) {
                hasInvalidPrice = true
            }
        })
    }

    return hasInvalidPrice
}

const getOrderSubTotal = function () {
    let cartItems = props.cartItems
    let totalValue = 0

    cartItems.forEach(function (item) {
        totalValue += item.subtotal
    })

    return totalValue
}
</script>

<template>
    <template v-for="(cartItem, productTitle) in groupedCartItems" :key="productTitle">
        <v-list-item>
            <template v-slot:prepend>
                <v-img v-if="cartItem.single[0]?.product_variation.product.medias[0]"
                       :src="cartItem.single[0].product_variation.product.medias[0]?.url"
                       cover width="60" max-width="60"/>

                <v-img v-if="Object.values(cartItem.variations)[0]?.[0]?.product_variation.product.medias[0]"
                       :src="Object.values(cartItem.variations)[0][0].product_variation.product.medias[0]?.url"
                       cover width="60" max-width="60"/>
            </template>
            <c-link :href="routeToProductDetails(cartItem)" @click="emit('close-drawer')">
                <span class="cursor-pointer">
                        <v-list-item-title class="pl-2 text-h6">{{ productTitle }}</v-list-item-title>
                        <v-list-item-subtitle class="pl-2">{{ cartItem.productReference }}</v-list-item-subtitle>
                    </span>
            </c-link>
            <template v-slot:append>
                <v-sheet class="text-end align-center" style="margin-right: -15px" v-if="!compact">
                    <p v-if="cartItem.manufacturing_days"><strong>Tempo de Produção:</strong> {{cartItem.manufacturing_days}} {{cartItem.manufacturing_days > 1 ? "dias" : "dia" }} </p>
                </v-sheet>
            </template>
        </v-list-item>

        <!-- Percorre as variações (produtos com 2 atributos) -->
        <v-list-group
            v-for="(colorGroup, color) in cartItem.variations"
            :key="color"
            base-color="transparent"
            :ripple="false"
        >
            <template v-slot:activator="{ props, isOpen}">
                <v-list-item v-bind="props" class="pa-0">
                    <v-row>
                        <v-col cols="2" class="d-flex align-center justify-end">
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <v-icon v-bind="props" color="grey-darken-3">
                                        {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
                                    </v-icon>
                                </template>
                                <span>{{ isOpen ? 'Recolher' : 'Expandir' }}</span>
                            </v-tooltip>
                        </v-col>
                        <v-col cols="10" class="pl-0 d-flex align-center">
                            <v-img
                                v-if="colorGroup[0].img_url ?? colorGroup[0].product_variation.medias[0]?.url"
                                :src="colorGroup[0].img_url ?? colorGroup[0].product_variation.medias[0]?.url"
                                max-width="50"
                                cover
                            />
                            <v-sheet class="ml-3">
                                <v-list-item-title class="font-weight-bold">
                                    {{ calculateVariationsQuantity(cartItem.variations, color) }} x {{ helpers.firstLetterUppercase(color) }}
                                </v-list-item-title>
                                <v-list-item-subtitle style="font-size: 0.7rem">clique para expandir</v-list-item-subtitle>
                            </v-sheet>
                        </v-col>
                    </v-row>
                    <template v-slot:append>
                        <v-sheet class="text-end" style="line-height: 1.2rem" v-if="colorGroup[0]?.price" width="130">
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                        <span v-bind="props" class="font-weight-bold"
                                              :class="{'text-warning': anyVariationHasInvalidPrice(cartItem.variations, color)}">
                                        R$ {{ helpers.formatPrice(calculateVariationsPrice(cartItem.variations, color)) }}
                                            <v-icon v-show="anyVariationHasInvalidPrice(cartItem.variations, color)" icon="mdi-alert" size="16"
                                                    color="warning"/>
                                        </span>
                                </template>
                                <span>Uma ou mais variações deste produto estão com preço inválido</span>
                            </v-tooltip>
                            <br/>
                            <template v-if="colorGroup[0]?.price !== colorGroup[0]?.original_price">
                                <span class="font-weight-bold text-medium-emphasis text-caption text-decoration-line-through">
                                    R$ {{ helpers.formatPrice(colorGroup[0]?.original_price) }} / un
                                </span><br/>
                            </template>
                            <span class="font-weight-bold text-medium-emphasis text-caption">
                                R$ {{ helpers.formatPrice(colorGroup[0]?.price) }} /un
                                </span>
                        </v-sheet>
                        <v-sheet v-else width="130"></v-sheet>
                    </template>
                </v-list-item>
            </template>

            <!-- Lista de produtos dentro dessa variação -->
            <v-list-item
                v-for="cartItem in colorGroup"
                :key="cartItem.product_variation.id || cartItem.product_variation_id"
                height="60px"
            >
                <v-row :style="`padding-left: 7%; margin-bottom: 25px;`" class="mt-2">
                    <v-col cols="1" class="align-self-center mt-1">
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-icon
                                    v-bind="props"
                                    variant="plain"
                                    icon="mdi-trash-can"
                                    size="18"
                                    color="grey-darken-2"
                                    @click.once="cartItem.id ? deleteCartItem(cartItem.id) : deleteSessionItem(cartItem.product_variation.id)"
                                />
                            </template>
                            <span>Remover item</span>
                        </v-tooltip>
                    </v-col>
                    <v-col cols="4" class="align-center d-flex me-2">
                        <c-link :href="routeToProductDetails(cartItem, cartItem.product_variation.attribute_values[1].slug)" @click="emit('close-drawer')">
                            <v-sheet class="d-inline-block" style="vertical-align: middle">
                                <v-list-item-title class="font-weight-bold" style="font-size: 0.9rem">
                                    Tamanho: {{ cartItem.size?.toUpperCase() ?? '' }}
                                </v-list-item-title>
                                <v-list-item-subtitle style="font-size: 0.7rem">{{ cartItem.product_variation.reference }}</v-list-item-subtitle>
                            </v-sheet>
                        </c-link>
                    </v-col>
                    <v-col cols="3" class="d-flex justify-start align-center mb-2">
                        <v-number-input
                            v-model="cartItem.quantity"
                            @update:modelValue="updateVariantQuantity(cartItem)"
                            :min="1"
                            :max="999999"
                            :step="10"
                            variant="underlined"
                            density="compact"
                            control-variant="stacked"
                            hide-details
                            inset
                            class="number-picker custom-controls"
                            max-width="120px"
                        />
                    </v-col>
                </v-row>
            </v-list-item>
        </v-list-group>

        <!-- Produtos com apenas 1 atributo (single) -->
        <v-list-item v-for="cartItem in cartItem.single"
                     :key="cartItem.product_variation.id || cartItem.product_variation_id" class="px-0">
            <v-row>
                <v-col cols="2" class="d-flex align-center justify-end">
                    <v-tooltip location="top">
                        <template v-slot:activator="{ props }">
                            <v-icon
                                v-bind="props"
                                variant="plain"
                                icon="mdi-trash-can"
                                size="18"
                                color="grey-darken-2"
                                @click.once="cartItem.id ? deleteCartItem(cartItem.id) : deleteSessionItem(cartItem.product_variation.id)"
                            />
                        </template>
                        <span>Remover item</span>
                    </v-tooltip>
                </v-col>
                <v-col cols="6" class="d-flex align-center justify-start pl-0">
                    <v-img
                        v-if="cartItem.img_url ?? cartItem.product_variation.medias[0]?.url"
                        :src="cartItem.img_url ?? cartItem.img_url ?? cartItem.product_variation.medias[0]?.url"
                        max-width="50"
                        cover
                    />
                    <c-link :href="routeToProductDetails(cartItem, cartItem.product_variation.attribute_values[0]?.slug)" @click="emit('close-drawer')">
                        <v-sheet class="d-inline-block ml-3" style="vertical-align: middle">
                            <v-list-item-title class="font-weight-bold">
                                {{
                                    cartItem.product_variation?.attribute_values?.[0]?.attribute_id === 2
                                        ? "Tamanho:" + cartItem.product_variation?.attribute_values?.[0]?.title.toUpperCase()
                                        : helpers.firstLetterUppercase(
                                            cartItem.product_variation?.attribute_values?.[0]?.title ??
                                            "Sem Cor"
                                        )
                                }}
                            </v-list-item-title>
                            <v-list-item-subtitle style="font-size: 0.7rem">{{ cartItem.product_variation.reference }}</v-list-item-subtitle>
                        </v-sheet>
                        <template v-if="cartItem?.metadata">
                            <!--                            @todo - colocar tooltip "detalhes da customização" -->
                            <v-sheet class="d-inline-block ml-3" style="vertical-align: middle">
                                <v-icon @click="detailsDialog = true; dialogMetadata = cartItem.metadata" class="pl-10" icon="mdi-eye-outline"
                                        title="Detalhes do item" size="22"/>

                                <v-dialog v-model="detailsDialog" location="center" width="auto">
                                    <c-customized-product-details-modal v-model="detailsDialog" :metadata="dialogMetadata"/>
                                </v-dialog>
                            </v-sheet>
                        </template>
                    </c-link>
                </v-col>
                <v-col cols="4" class="d-flex align-center text-center">
                    <v-number-input
                        v-model="cartItem.quantity"
                        @update:modelValue="updateVariantQuantity(cartItem)"
                        :min="1"
                        :max="999999"
                        :step="10"
                        variant="underlined"
                        density="compact"
                        control-variant="stacked"
                        hide-details
                        inset
                        class="number-picker custom-controls mx-3"
                        max-width="120px"
                    />
                </v-col>
            </v-row>

            <template v-if="cartItem.price" v-slot:append>
                <v-sheet class="text-end" style="line-height: 1.2rem;" width="130">
                    <span class="font-weight-bold" style="font-size: 1rem">
                        R$ {{ helpers.formatPrice(cartItem.subtotal) }}
                    </span><br/>
                    <template v-if="cartItem.price !== cartItem.original_price">
                        <span class="font-weight-bold text-medium-emphasis text-caption text-decoration-line-through">
                            R$ {{ helpers.formatPrice(cartItem.original_price) }} / un
                        </span><br/>
                    </template>
                    <span class="font-weight-bold text-medium-emphasis text-caption">
                        R$ {{ helpers.formatPrice(cartItem.price) }} / un
                    </span>
                </v-sheet>
            </template>
        </v-list-item>
    </template>
    <v-sheet v-if="props.cartItems[0]?.price" class="d-flex justify-end align-center py-5 pe-4 mt-5" style="border-top: 1px solid #e0e0e0">
        <span class="font-weight-bold"> Total: R$ {{ helpers.formatPrice(getOrderSubTotal()) }}</span>
    </v-sheet>
</template>

<style scoped>
.custom-controls :deep(.v-number-input__control:last-child::after) {
    content: "+";
    font-weight: bold;
    color: #272727;
    position: absolute;
    height: 100%;
    z-index: -10;
}

.custom-controls :deep(.v-btn__overlay) {
    background-color: transparent !important;
}

.custom-controls :deep(.v-number-input__control:last-child::before) {
    content: "−";
    font-weight: bold;
    color: #272727;
    position: absolute;
    top: 47%;
    z-index: -10;
}

.custom-controls :deep(.v-number-input__control) {
    height: 3rem;
    width: 15px;
    position: relative;
    align-items: center;
}

.custom-controls :deep(.v-number-input__control i) {
    display: none;
}

.custom-controls :deep(.v-number-input__control .v-divider) {
    width: 15px;
    align-self: auto;
}

.number-picker {
    background-color: white !important;
    border: none !important;
    border-radius: 0 !important;
    align-items: center !important;
}

.number-picker :deep(.v-field__input) {
    padding-top: 0.5rem !important;
}

.number-picker :deep(.v-field__field) {
    align-items: center !important;
    padding-left: 0.3rem;
}

.number-picker :deep(.v-field__outline):after {
    border-width: 0 0 var(--v-field-border-width);
}
</style>